/**
 * Analytics TypeScript Definitions
 * Type-safe event tracking for product analytics
 */

/**
 * Page categories for navigation tracking
 */
export enum PageCategory {
  DASHBOARD = 'dashboard',
  COLLECTIONS = 'collections',
  ARTIFACTS = 'artifacts',
  NOTES = 'notes',
  CHAT = 'chat',
  SETTINGS = 'settings',
  AUTH = 'auth',
  MOBILE_CAPTURE = 'mobile_capture',
  ONBOARDING = 'onboarding',
  LIVE = 'live',
  AUDIT = 'audit',
  OTHER = 'other',
}

export type PageViewType = 'list' | 'detail';

/**
 * User properties for analytics user identification
 */
export interface UserProperties {
  // Identity — internal Prisma UUID (not Firebase UID)
  user_id: string;
  email: string;
  display_name?: string;
  is_internal?: boolean;
  email_domain?: string;

  // Workspace context
  workspace_id: string;
  // Raw (uncollapsed) role — includes the two read-only roles (`guest` is
  // the live read-only role, `viewer` is deprecated but still occurs).
  // Authorisation code must keep using the collapsed `admin|member|viewer`
  // role (`useWorkspaceRole` / backend `req.workspace.userRole`) — this
  // wider union is for analytics identification only.
  workspace_role?: 'owner' | 'admin' | 'member' | 'guest' | 'viewer';
  workspace_member_count?: number;

  // Usage metrics (updated periodically)
  total_projects?: number;
  total_notes?: number;
  total_attachments?: number;

  // Feature adoption flags
  has_used_ocr?: boolean;
  has_used_voice_recording?: boolean;
  has_used_mobile_app?: boolean;
  has_used_export?: boolean;

  // Retention tracking
  signup_date?: string;
  days_since_signup?: number;

  // Device/Platform
  primary_device?: 'mobile' | 'desktop';
  browser?: string;
}

// ── Creation Context ─────────────────────────────────────────────────
// Which control started an entity creation (import panel and import hook).

export type CreationTrigger =
  | 'create_modal'
  | 'quick_capture_tile'
  | 'add_button'
  | 'import_modal'
  | 'bulk_action'
  | 'agent_tool'
  | 'drag_drop'
  | 'paste'
  | 'kebab_menu'
  | 'voice_button'
  | 'camera_button';

// ── Base Event Properties ────────────────────────────────────────────

/**
 * Base event properties common to all events
 */
export interface BaseEventProperties {
  page_path?: string;
  page_category?: PageCategory;
  platform?: 'web' | 'mobile_web';
  timestamp?: string;
  [key: string]: unknown;
}

// ── AI Features ──────────────────────────────────────────────────────

export interface OcrTriggeredProperties extends BaseEventProperties {
  trigger_source: 'attachment_kebab' | 'action_button' | 'upload_flow';
  image_count: number;
  processing_time_ms?: number;
  /** 'async' when the note-ocr background job reads the photo (a note).
   * Absent on the synchronous `/ocr` path (a legacy protocol). */
  mode?: 'async';
}

export interface VoiceRecordingProperties extends BaseEventProperties {
  source: 'chat' | 'note' | 'mobile_capture';
  recording_type?: 'voice_to_note' | 'voice_to_chat';
  duration_seconds?: number;
  transcription_provider?: string;
  success?: boolean;
  /** Which step of the recording failed. Absent on non-failure events. */
  stage?: 'microphone' | 'recorder_setup' | 'upload';
  /**
   * DOMException name for a failure (`NotAllowedError`, `NotReadableError`, …).
   * A bounded browser enum, never user content — it is what separates "the user
   * denied us" from "their headset was busy", which one shared toast string
   * could not. Absent on non-failure events.
   */
  failure_reason?: string;
  /** Single speaker (dictation) or multi-speaker (meeting) transcription. */
  mode?: 'dictation' | 'meeting';
  workspace_id?: string;
  /**
   * 'async' when the note-transcription background job starts the
   * transcription (a note). Absent on the recorder path and on the
   * synchronous `/transcribe` path a legacy protocol keeps. Mirrors
   * `OcrTriggeredProperties.mode`.
   */
  delivery?: 'async';
  /** Whether this run is a user-initiated retry over an existing transcript. */
  rerun?: boolean;
}

// ── Export & Import ──────────────────────────────────────────────────

export interface ExportProperties extends BaseEventProperties {
  export_type: 'pdf' | 'docx' | 'markdown' | 'text' | 'json';
  entity_type: 'note' | 'collection' | 'protocol';
  export_method: 'browser_print' | 'client_export' | 'client_docx' | 'server_export';
  success?: boolean;
  file_size_kb?: number;
  /**
   * Completed, but with something left out (Word export: a node type with no
   * converter, an image that could not be embedded, a cell colour with no
   * Word equivalent). `success: true` alone would read as a clean export.
   */
  lossy?: boolean;
  flattened_node_types?: string[];
  skipped_images?: number;
  unshaded_cells?: number;
  /** Protocol exports: step media in the file, and those printed as a link only. */
  media_count?: number;
  media_without_preview?: number;
  /** Protocol exports: the read hit its step cap, or a step body was unreadable. */
  incomplete?: boolean;
  unreadable_steps?: number;
  /** Protocol exports: the page's "did not come through" notices, repeated in the file. */
  notices?: number;
}

// ── Bird Chat — Data & Visualisation ─────────────────────────────────

export interface ChartRenderedProperties extends BaseEventProperties {
  chart_type: 'bar' | 'line' | 'area' | 'scatter';
  series_count: number;
  data_points: number;
}

/**
 * A chart Bird rendered was taken out of the thread. One event for every route
 * out, discriminated by `format`; `destination` splits the clipboard from a
 * file so we can see whether the copy path is actually usable in the field (it
 * has no fallback on an insecure origin). Shape only — no axis labels, no data.
 */
export interface ChartExportedProperties extends BaseEventProperties {
  format: 'png' | 'svg' | 'csv';
  destination: 'clipboard' | 'file';
  chart_type: 'bar' | 'line' | 'area' | 'scatter';
  series_count: number;
  data_points: number;
  succeeded: boolean;
}
